import dayjs from 'dayjs'
import { AppointmentRoom, FeedbackRoom, Room } from 'src/models/Room'
import useSWR from 'swr'
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite'
import { ApiClient, fetcher } from '../client'
import {
  GET_DELETED_GROUPS,
  GET_ROOMS,
  GET_ROOMS_BEFORE_APPOINTMENT,
  GET_ROOMS_BEFORE_FEEDBACK,
  GET_ROOMS_BEFORE_FEEDBACK_RECEIVED_LIST,
} from '../endpoints'
import {
  AppointmentRoomResponse,
  FeedbackRoomResponse,
  FilterCondition,
  RoomQueryableUniqueId,
  RoomResponseBase,
  RoomsResponse,
} from '../types/room'
import { asInfinite } from '../utils'
import {
  toAppointmentRoom,
  toFeedbackRoom,
  toRooms,
  toRoomsQuery,
} from './converters'

const roomsFetcher = async (
  url: string,
  page: number,
  userId: number,
): Promise<Room[]> => {
  const res: RoomsResponse = await fetcher(
    `${url}${url.includes('?') ? '&' : '?'}page=${page + 1}`,
  )
  return toRooms(
    [res.pinnedRooms, res.rooms, res.unreadBatchMessageRooms].flat(),
    userId,
  )
}
const roomFetcher = async (url: string, userId: number): Promise<Room[]> => {
  const res: RoomsResponse = await fetcher(url)
  return toRooms(res.rooms, userId)
}
export const ROOMS_PAGE_SIZE = 20
export const useRooms = asInfinite(
  (
    param: { query: FilterCondition; userId: number },
    pageSize: number,
    config?: SWRInfiniteConfiguration,
  ) =>
    // NOTE: userIdがundefinedのときはfetchしない
    useSWRInfinite<Room[]>(
      (index) =>
        param.userId
          ? [
              `${GET_ROOMS}${toRoomsQuery({ ...param.query, size: pageSize })}`,
              index,
              param.userId,
            ]
          : null,
      roomsFetcher,
      config,
    ),
)

export const useRoom = (
  roomId: RoomQueryableUniqueId | undefined,
  userId: number,
) =>
  useSWR(
    roomId // won't fetch when roomId is undefined
      ? [`${GET_ROOMS}${toRoomsQuery({ roomId, size: 1 })}`, userId]
      : null,
    roomFetcher,
  )

const feedbackRoomsFetcher = async (
  url: string,
): Promise<useFeedbackRoomsType> => {
  const res = await ApiClient.get<FeedbackRoomResponse>(url)
  if (res.error)
    return {
      feedbackRooms: [],
      feedbackArticleId: undefined,
    }
  return {
    feedbackRooms: res.data?.roomsBeforeFeedback.map(toFeedbackRoom) || [],
    feedbackArticleId: res.data?.feedbackArticleId || undefined,
  }
}
type useFeedbackRoomsType = {
  feedbackRooms: FeedbackRoom[]
  feedbackArticleId?: number
}
export const useFeedbackRooms = () =>
  useSWR<useFeedbackRoomsType>(GET_ROOMS_BEFORE_FEEDBACK, feedbackRoomsFetcher)

const receivedFeedbackRoomsFetcher = async (
  url: string,
): Promise<useFeedbackRoomsType> => {
  const res = await ApiClient.get<FeedbackRoomResponse>(url)
  if (res.error)
    return {
      feedbackRooms: [],
    }
  return {
    feedbackRooms: res.data?.roomsBeforeFeedback.map(toFeedbackRoom) || [],
  }
}
type ReceiveduseFeedbackRoomsType = {
  feedbackRooms: FeedbackRoom[]
}
export const useReceivedFeedbackRooms = () =>
  useSWR<ReceiveduseFeedbackRoomsType>(
    GET_ROOMS_BEFORE_FEEDBACK_RECEIVED_LIST,
    receivedFeedbackRoomsFetcher,
  )

const appointmentRoomsFetcher = async (url: string) => {
  const res = await ApiClient.get<AppointmentRoomResponse>(url)
  if (res.error) return []
  return res.data?.roomsBeforeAppointment.map(toAppointmentRoom) || []
}

export const useAppointmentRooms = () =>
  useSWR<AppointmentRoom[]>(
    GET_ROOMS_BEFORE_APPOINTMENT,
    appointmentRoomsFetcher,
  )

// Referrer only
const deletedRoomSorter = (a: RoomResponseBase, b: RoomResponseBase) => {
  return dayjs(b.updatedAt).unix() - dayjs(a.updatedAt).unix()
}
const deletedRoomsFetcher = async (
  url: string,
  userId: number,
  isReferrer: boolean,
): Promise<Room[]> => {
  if (!isReferrer) return []
  const res: RoomsResponse = await fetcher(url)
  return toRooms((res?.rooms || []).sort(deletedRoomSorter), userId)
}
export const useDeletedGroups = (userId: number, isReferrer: boolean) =>
  useSWR<Room[]>([GET_DELETED_GROUPS, userId, isReferrer], deletedRoomsFetcher)
